// src/components/Footer.jsx
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../utils/api";
import FeedbackModal from "./FeedbackModal";
//import FeedbackModal from "./components/FeedbackModal";

const HIDE_FEEDBACK_ON = [
  "/payment",
  "/payment-success",
  "/booking-confirmation",
  "/admin",
  "/driver",
  "/feedback",
];

const shouldHideFeedback = (path) =>
  HIDE_FEEDBACK_ON.some((p) => path.startsWith(p));

const Footer = (props) => {
  const location = useLocation();
  const siteMode = props?.siteMode || process.env.REACT_APP_SITE_MODE || "ccg";
  const isPlanner =
    props?.isPlanner || siteMode === "planner";
  const isMinibusGrab = siteMode === "minibusgrab";
  const isCoachGrab = siteMode === "cheapcoachgrab";
  const brandName = isMinibusGrab ? "MinibusGrab" : isCoachGrab ? "CheapCoachGrab" : "CheapCabGrab";

  const [showModal, setShowModal] = useState(false);

  const hideFeedback = shouldHideFeedback(location.pathname);

  const handleFeedbackClick = async () => {
    // Capture context at click time
    const ctx = {
      from_path: location.pathname,
      from_url: window.location.href,
      from_title: document.title,
      ts: Date.now(),
    };

    // store briefly so feedback page can load it
    sessionStorage.setItem("ccg_feedback_context", JSON.stringify(ctx));

    // Try to fetch a short-lived feedback token
    try {
      const res = await api.post("/api/feedback-token", {
        page_path: ctx.from_path,
        page_title: ctx.from_title
      });
      if (res.data?.token) {
        sessionStorage.setItem("ccg_feedback_token", res.data.token);
      }
    } catch (err) {
      // Ignore token errors; form will still show but submit will fail without token
      console.warn("Feedback token fetch failed", err?.message || err);
    }

    // Open modal instead of redirect
    setShowModal(true);
  };

  return (
    <>
      {!hideFeedback && (
        <div style={styles.feedbackStrip}>
          <div style={styles.feedbackStripInner}>
            <div style={styles.feedbackText}>
              Help us improve these travel tools — what’s missing or confusing?
            </div>
            <button style={styles.feedbackButton} onClick={handleFeedbackClick}>
              Give feedback
            </button>
          </div>
        </div>
      )}

      <footer style={styles.footer}>
        <div style={styles.mainLinks}>
          <Link to="/contact" style={styles.link}>Contact</Link>
          <Link to="/privacy" style={styles.link}>Privacy Policy</Link>
          <Link to="/terms" style={styles.link}>Terms</Link>
        </div>

        {isPlanner ? (
          <div style={styles.plannerBlock}>
            <div style={styles.plannerTitle}>Airport Journey Planner</div>
            <div style={styles.plannerText}>
              Live road, rail and flight intelligence for UK airport journeys.
            </div>
            <div style={styles.partnerNote}>Powered by CheapCabGrab.</div>
          </div>
        ) : (
          <div style={styles.arrivalLinks}>
            {isMinibusGrab || isCoachGrab ? (
              <>
                <span style={styles.arrivalLabel}>{brandName}:</span>
                <Link to={isMinibusGrab ? "/minibus-hire" : "/coach-hire"} style={styles.link}>
                  {isMinibusGrab ? "Minibus Hire" : "Coach Hire"}
                </Link>
                <Link to={isMinibusGrab ? "/heathrow-minibus" : "/heathrow-coach-hire"} style={styles.link}>
                  Heathrow {isMinibusGrab ? "Minibus" : "Coach"}
                </Link>
                <Link to="/contact" style={styles.link}>Support</Link>
              </>
            ) : (
              <>
                <span style={styles.arrivalLabel}>Arrival Assistants:</span>
                <Link to="/international-students/uk-arrival-guide" style={styles.link}>International Student Guide</Link>
                <Link to="/arrival-assistant-visitor" style={styles.link}>Visitors</Link>
                <Link to="/arrival-assistant-worker" style={styles.link}>Workers</Link>
              </>
            )}
          </div>
        )}

        {!isPlanner && (
          <div style={styles.partnerBlock}>
            {isMinibusGrab || isCoachGrab ? (
              <>
                <div style={styles.partnerTitle}>{brandName}</div>
                <div style={styles.partnerList}>
                  Group transport enquiries and bookings handled through the CheapCabGrab platform.
                </div>
                <div style={styles.partnerNote}>
                  Operated with approved partner operators including RHP Minibus Hire UK Ltd where relevant.
                </div>
              </>
            ) : (
              <>
                <div style={styles.partnerTitle}>Official Associated Domains</div>
                <div style={styles.partnerList}>
                  cheapcabgrab.co.uk · cheapcoachgrab.com · cheapcoachgrab.co.uk · cheapminibusgrab.com ·
                  cheapminibusgrab.co.uk · minibusgrab.com · minibusgrab.co.uk
                </div>
                <div style={styles.partnerNote}>
                  Operated by CheapCabGrab Ltd in partnership with RHP Minibus Hire UK Ltd.
                </div>
              </>
            )}
          </div>
        )}
      </footer>
      {showModal && <FeedbackModal onClose={() => setShowModal(false)} />}
    </>
  );
};

const styles = {
  feedbackStrip: {
    background: "#fff8d6",
    borderTop: "1px solid #f0e1a0",
    borderBottom: "1px solid #f0e1a0",
    padding: "10px 12px",
  },
  feedbackStripInner: {
    maxWidth: "980px",
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  },
  arrivalLinks: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "6px",
    flexWrap: "wrap",
  },
  arrivalLabel: {
    color: "#fff",
    opacity: 0.85,
    fontSize: "13px",
  },
  plannerBlock: {
    marginTop: "8px",
    fontSize: "13px",
    color: "#ddd",
    lineHeight: "1.6",
  },
  plannerTitle: {
    color: "#fff",
    fontWeight: 700,
    marginBottom: "4px",
  },
  plannerText: {
    marginBottom: "4px",
  },
  feedbackText: {
    color: "#222",
    fontSize: "14px",
    fontWeight: 600,
  },
  feedbackButton: {
    background: "#f4c400",
    border: "none",
    color: "#111",
    fontWeight: 700,
    padding: "10px 14px",
    borderRadius: "10px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  footer: {
    textAlign: "center",
    padding: "15px",
    backgroundColor: "#222",
    color: "#fff",
    marginTop: "0px",
  },
  mainLinks: { marginBottom: "8px" },
  link: { margin: "0 15px", color: "#fff", textDecoration: "none" },
  partnerBlock: { marginTop: "10px", fontSize: "11px", color: "#ccc", lineHeight: "1.4" },
  partnerTitle: { fontWeight: "600", marginBottom: "4px" },
  partnerList: { marginBottom: "3px" },
  partnerNote: { fontStyle: "italic", opacity: 0.9 },
};

export default Footer;
